import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, Calendar } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';

const ProjectionLineChart = ({ data, selectedTimeframe, onTimeframeChange, currentProjection, totalCapital, monthlyContribution }) => {
  const { t } = useLanguage();
  
  const timeframeOptions = [
    { value: '365', label: t('horizon1y') },
    { value: '1095', label: t('horizon3y') },
    { value: '1825', label: t('horizon5y') }
  ];

  // B) Helper function to downsample data based on timeframe
  const getDownsampledData = (originalData, timeframe) => {
    if (!originalData || originalData.length === 0) return [];
    
    const days = parseInt(timeframe);
    let sampledData = [];
    
    if (days === 365) {
      // 1 año: show monthly points (13 points: 0,1,2...12 months)
      for (let month = 0; month <= 12; month++) {
        const targetDay = Math.floor((month * days) / 12);
        const dataPoint = originalData.find(d => Math.abs(d.day - targetDay) <= 15) || originalData[Math.min(month * Math.floor(originalData.length / 12), originalData.length - 1)];
        if (dataPoint) {
          sampledData.push({ ...dataPoint, period: month, periodType: 'month' });
        }
      }
    } else if (days === 1095) {
      // 3 años: show quarterly points (13 points: 0,3,6,9...36 months)
      for (let quarter = 0; quarter <= 12; quarter++) {
        const targetDay = Math.floor((quarter * days) / 12);
        const dataPoint = originalData.find(d => Math.abs(d.day - targetDay) <= 30) || originalData[Math.min(quarter * Math.floor(originalData.length / 12), originalData.length - 1)];
        if (dataPoint) {
          sampledData.push({ ...dataPoint, period: quarter, periodType: 'quarter' });
        }
      }
    } else {
      // 5 años: show yearly points (6 points: 0,1,2,3,4,5 years)
      for (let year = 0; year <= 5; year++) {
        const targetDay = Math.floor((year * days) / 5);
        const dataPoint = originalData.find(d => Math.abs(d.day - targetDay) <= 60) || originalData[Math.min(year * Math.floor(originalData.length / 5), originalData.length - 1)];
        if (dataPoint) {
          sampledData.push({ ...dataPoint, period: year, periodType: 'year' });
        }
      }
    }
    
    return sampledData;
  };

  // Get downsampled data
  const chartData = getDownsampledData(data, selectedTimeframe);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (point) => {
    if (!point.periodType) return new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    // B) Format labels based on sampled cadence
    switch (point.periodType) {
      case 'month':
        return point.period === 0 ? 'Inicio' : `${point.period}M`;
      case 'quarter':
        return point.period === 0 ? 'Inicio' : `T${point.period}`;
      case 'year':
        return point.period === 0 ? 'Inicio' : `${point.period}A`;
      default:
        return new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // D) Chart dimensions with mobile responsive min-height
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const chartWidth = 600;
  const chartHeight = Math.max(isMobile ? 280 : 300, 280); // D) min-height ~260-300px in mobile
  const padding = { top: 20, right: 40, bottom: 40, left: 80 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  if (!chartData || chartData.length === 0) {
    return (
      <Card className="bg-[#0b1c38] border-[#12b7ff] shadow-xl">
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-gray-400">Loading projection data...</div>
        </CardContent>
      </Card>
    );
  }

  // Find min/max values for scaling
  const allValues = chartData.map(d => d.value);

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue;
  const minDisplay = Math.max(0, minValue - valueRange * 0.1);
  const maxDisplay = maxValue + valueRange * 0.1;

  // Create scales
  const xScale = (index) => (index / (chartData.length - 1)) * innerWidth;
  const yScale = (value) => innerHeight - ((value - minDisplay) / (maxDisplay - minDisplay)) * innerHeight;

  // Create paths for different series
  const createPath = (dataArray) => {
    return dataArray.map((point, index) => {
      const x = xScale(index);
      const y = yScale(point.value);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };

  const createAreaPath = (dataArray) => {
    const linePath = createPath(dataArray);
    return `M 0 ${innerHeight} L ${linePath.substring(2)} L ${innerWidth} ${innerHeight} Z`;
  };

  // Main path for total portfolio value
  const totalPath = createPath(chartData);
  const totalAreaPath = createAreaPath(chartData);

  // Y-axis labels
  const yAxisLabels = [];
  const labelCount = 4;
  for (let i = 0; i <= labelCount; i++) {
    const value = minDisplay + (maxDisplay - minDisplay) * (i / labelCount);
    yAxisLabels.push({
      value,
      y: innerHeight - (i / labelCount) * innerHeight,
      label: formatCurrency(value)
    });
  }

  // X-axis labels
  const xAxisLabels = [];
  const labelIndices = [0, Math.floor(chartData.length / 3), Math.floor(2 * chartData.length / 3), chartData.length - 1];
  labelIndices.forEach(index => {
    if (chartData[index]) {
      xAxisLabels.push({
        x: xScale(index),
        label: formatDate(chartData[index])
      });
    }
  });

  const finalValue = chartData[chartData.length - 1]?.value || 0;
  const initialValue = totalCapital || chartData[0]?.value || 0;
  const totalGain = finalValue - initialValue - (monthlyContribution * Math.round(parseInt(selectedTimeframe) / 30.44));
  const totalContributions = (monthlyContribution * Math.round(parseInt(selectedTimeframe) / 30.44));
  const totalInvested = initialValue + totalContributions;
  const totalGainPercent = totalInvested > 0 ? ((totalGain / totalInvested) * 100).toFixed(1) : 0;

  return (
    <Card className="bg-[#0b1c38] border-[#12b7ff] shadow-xl">
      <CardHeader>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <CardTitle className="text-[#00f4ff] flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            {t('portfolioGrowthProjection')}
          </CardTitle>
          
          {/* Timeframe selector */}
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-[#0677aa]" />
            <div className="flex gap-2">
              {timeframeOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={selectedTimeframe === option.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => onTimeframeChange(option.value)}
                  className={`transition-all ${selectedTimeframe === option.value 
                    ? "bg-[#00f4ff] text-[#040816] hover:bg-[#12b7ff] shadow-lg shadow-[#00f4ff]/20"
                    : "border-[#0677aa] text-[#9EB4C0] hover:bg-[#0b1c38] hover:border-[#00f4ff] hover:text-[#00f4ff]"
                  }`}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Key Metrics with modern cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="relative overflow-hidden bg-gradient-to-br from-[#0b1c38] to-[#040816] p-4 rounded-xl border border-[#00f4ff]/20 shadow-lg">
            <div className="absolute top-0 right-0 w-20 h-20 bg-[#00f4ff]/5 rounded-full blur-2xl"></div>
            <div className="relative">
              <div className="text-[#9EB4C0] text-xs uppercase tracking-wide font-medium mb-1">{t('finalValue')}</div>
              <div className="text-2xl font-bold text-[#00f4ff]">
                {formatCurrency(finalValue)}
              </div>
            </div>
          </div>
          <div className="relative overflow-hidden bg-gradient-to-br from-[#0b1c38] to-[#040816] p-4 rounded-xl border border-green-400/20 shadow-lg">
            <div className="absolute top-0 right-0 w-20 h-20 bg-green-400/5 rounded-full blur-2xl"></div>
            <div className="relative">
              <div className="text-[#9EB4C0] text-xs uppercase tracking-wide font-medium mb-1">{t('totalGain')}</div>
              <div className="text-2xl font-bold text-green-400">
                {formatCurrency(totalGain)}
              </div>
            </div>
          </div>
          <div className="relative overflow-hidden bg-gradient-to-br from-[#0b1c38] to-[#040816] p-4 rounded-xl border border-green-400/20 shadow-lg">
            <div className="absolute top-0 right-0 w-20 h-20 bg-green-400/5 rounded-full blur-2xl"></div>
            <div className="relative">
              <div className="text-[#9EB4C0] text-xs uppercase tracking-wide font-medium mb-1">{t('returnPercent')}</div>
              <div className="text-2xl font-bold text-green-400">
                +{totalGainPercent}%
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-gradient-to-br from-[#040816] to-[#0b1c38] p-6 rounded-xl border border-[#00f4ff]/20 overflow-x-auto shadow-inner">
          <svg width={chartWidth} height={chartHeight} className="w-full max-w-full">
            <defs>
              {/* Modern gradient for area fill */}
              <linearGradient id="portfolioGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#00f4ff" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#12b7ff" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#0677aa" stopOpacity="0.05" />
              </linearGradient>
              
              {/* Glow filter for line */}
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            <g transform={`translate(${padding.left}, ${padding.top})`}>
              {/* Grid lines */}
              {yAxisLabels.map((label, index) => (
                <g key={index}>
                  <line 
                    x1="0" 
                    y1={label.y} 
                    x2={innerWidth} 
                    y2={label.y} 
                    stroke="rgba(0,244,255,0.08)" 
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  <text 
                    x="-10" 
                    y={label.y + 4} 
                    textAnchor="end" 
                    fill="#9EB4C0" 
                    fontSize="11"
                    fontWeight="500"
                  >
                    {label.label}
                  </text>
                </g>
              ))}
              
              {/* Area fill under the line */}
              <path 
                d={totalAreaPath} 
                fill="url(#portfolioGradient)" 
              />
              
              {/* Main portfolio line with glow */}
              <path 
                d={totalPath} 
                stroke="#00f4ff" 
                strokeWidth="3" 
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#glow)"
              />
              
              {/* Data points with modern styling */}
              {chartData.map((point, index) => {
                // Show points at key intervals
                if (index === 0 || index === chartData.length - 1 || index % Math.max(1, Math.floor(chartData.length / 4)) === 0) {
                  return (
                    <g key={index}>
                      {/* Outer glow circle */}
                      <circle
                        cx={xScale(index)}
                        cy={yScale(point.value)}
                        r="8"
                        fill="#00f4ff"
                        opacity="0.2"
                      />
                      {/* Main point */}
                      <circle
                        cx={xScale(index)}
                        cy={yScale(point.value)}
                        r="4"
                        fill="#00f4ff"
                        stroke="#040816"
                        strokeWidth="2"
                        className="transition-all"
                      />
                    </g>
                  );
                }
                return null;
              })}
              
              {/* X-axis labels */}
              {xAxisLabels.map((label, index) => (
                <text 
                  key={index}
                  x={label.x} 
                  y={innerHeight + 25} 
                  textAnchor="middle" 
                  fill="#9EB4C0" 
                  fontSize="11"
                  fontWeight="500"
                >
                  {label.label}
                </text>
              ))}
            </g>
          </svg>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectionLineChart;